import { useUserSettings } from "@next-platform/client-common-core";
import { useCallback, useState } from "react";

import { ANALYTICS_PROD_CDN } from "@/pages/CreatorAnalytics/constants";
import { AvatarPreset } from "@/types";
import { resolveDevicePixelRatioPreset } from "@/utils/getAvatarUrl";

interface ICharacterAvatarProps {
  characterId: string;
  name: string;
  avatarUrl?: string | null;
  isNsfw?: boolean;
  size?: "sm" | "md";
}

const SIZE_CLASSES = {
  sm: "h-9 w-9 rounded-lg",
  md: "h-16 w-16 rounded-xl",
} as const;

const AVATAR_PRESET_BY_SIZE = {
  sm: AvatarPreset.AVATAR_128X128,
  md: AvatarPreset.AVATAR_256X256,
} as const;

// Creator Analytics avatar keys always point at prod-only assets (see
// ANALYTICS_PROD_CDN), so this resolves against a fixed prod host instead of
// the environment-aware getAvatarUrl, while still applying its DPR upscaling.
const resolveAnalyticsAvatar = (relativePath: string, size: "sm" | "md"): string => {
  const preset = resolveDevicePixelRatioPreset(AVATAR_PRESET_BY_SIZE[size]);
  return `${ANALYTICS_PROD_CDN}/${relativePath}?class=${preset}`;
};

const CharacterAvatar = ({ name, avatarUrl, isNsfw = false, size = "sm" }: ICharacterAvatarProps) => {
  const { blurNSFW } = useUserSettings();
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => setHasError(true), []);

  const resolved = avatarUrl && !hasError ? resolveAnalyticsAvatar(avatarUrl, size) : null;
  const isBlurred = blurNSFW && isNsfw;
  const sizeClass = SIZE_CLASSES[size];

  return resolved ? (
    <img src={resolved} alt={name} onError={handleError} className={`${sizeClass} shrink-0 object-cover ${isBlurred ? "blur" : ""}`} />
  ) : (
    <div className={`${sizeClass} shrink-0 border border-default-200 bg-default-100 dark:border-default-100`} />
  );
};

export default CharacterAvatar;
